import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { TextareaModule } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';
import { finalize, debounceTime, distinctUntilChanged, Subscription, forkJoin } from 'rxjs';

// Composants
import { FormBaseComponent } from 'src/app/@shared/components/abstract/form-base.component';
import { AvatarComponent } from 'src/app/@shared/components/user/avatar/avatar.component';
import { ConversationItem } from 'src/app/@shared/models/messaging/conversation-item.model';

// Modèles
import { Conversation } from 'src/app/@shared/models/messaging/conversation.model';
import { File as FileModel } from 'src/app/@shared/models/file/file.model';
import { ClientContact } from 'src/app/@shared/models/client/contact.model';
import { PartnerContact } from 'src/app/@shared/models/partner/contact.model';

// Utils
import { Logger } from 'src/app/@core/services/logger/logger.service';
import { Filter } from 'src/app/@shared/utilities/models/filter';

// Candidat à la mention (interne = ClientContact, externe = PartnerContact)
interface MentionCandidate {
	id: number;
	label: string;
	isInternal: boolean;
	contact: ClientContact | PartnerContact;
}

const LOGGER = new Logger('ConversationItemFormComponent');

@Component({
	selector: 'app-conversation-item-form',
	imports: [
		CommonModule,
		ReactiveFormsModule,
		ButtonModule,
		TextareaModule,
		TooltipModule,
		AvatarComponent
	],
	templateUrl: './conversation-item-form.component.html',
	styleUrl: './conversation-item-form.component.scss'
})
export class ConversationItemFormComponent extends FormBaseComponent {

	/*----------------------------------------------------------------------------------------
	 * Gestion de la vue
	 ----------------------------------------------------------------------------------------*/

	@Output() public conversationCreated: EventEmitter<Conversation> = new EventEmitter<Conversation>();
	@Output() public itemSaved: EventEmitter<ConversationItem> = new EventEmitter<ConversationItem>();

	@ViewChild('contentTextarea') private _contentTextarea?: ElementRef<HTMLTextAreaElement>;

	private _currentDraftItem?: ConversationItem;
	private _creatingDraft = false;
	private _persistingConversation = false;

	// Mentions
	public activeMentionIndex: number = 0;
	public filteredMentionCandidates: MentionCandidate[] = [];
	public mentionOverlayVisible: boolean = false;
	private _mentionCandidates: MentionCandidate[] = [];
	private _mentionTokenStart: number = -1;
	private _selectedMentions: MentionCandidate[] = [];

	/*----------------------------------------------------------------------------------------
	 * Données
	 ----------------------------------------------------------------------------------------*/

	private _attachments: FileModel[] = [];
	public get attachments(): FileModel[] {
		return this._attachments;
	}
	public set attachments(value: FileModel[]) {
		this._attachments = [...value];
	}

	private _conversation!: Conversation;
	public get conversation(): Conversation {
		return this._conversation;
	}
	@Input()
	public set conversation(value: Conversation) {
		LOGGER.debug('Nouvelle conversation assignée', { conversationId: value?.id });
		this._conversation = value;
		this._initializeForm();
	}

	/*----------------------------------------------------------------------------------------
	 * Accesseurs
	 ----------------------------------------------------------------------------------------*/

	/** Envoyer un message est une activité à part, contrôlée par le serveur (#1218) */
	public get canSendMessage(): boolean {
		return !!this.appService.user?.hasAuthorization(this.Authorizations.ConversationItemSend);
	}

	public get isSendDisabled(): boolean {
		const content = this.form.get('content')?.value || '';
		const isEmpty = content.trim().length === 0 && this.attachments.length === 0;
		// Bloque l'envoi tant que le brouillon n'est pas sauvegardé :
		// - form.dirty : modifications non encore persistées (auto-save en attente/en cours)
		// - _creatingDraft : création du brouillon en cours
		return isEmpty || this.form.dirty || this._creatingDraft;
	}

	/*----------------------------------------------------------------------------------------
	 * Méthodes publiques
	 ----------------------------------------------------------------------------------------*/

	public getTruncatedFileName(fileName: string): string {
		const maxLength = 15;
		if (fileName.length <= maxLength) {
			return fileName;
		}
		return fileName.substring(0, maxLength) + '...';
	}

	public onAttachmentClick(attachment: FileModel): void {
		this._handleAttachmentClick(attachment);
	}

	public onContentClick(): void {
		this._updateMentionOverlay();
	}

	public onContentKeydown(event: KeyboardEvent): void {
		this._handleMentionKeydown(event);
	}

	public onContentKeyup(event: KeyboardEvent): void {
		// On ne recalcule pas l'overlay sur les touches de navigation (gérées en keydown)
		if (this.mentionOverlayVisible && ['ArrowUp', 'ArrowDown', 'Enter', 'Escape'].includes(event.key)) {
			return;
		}
		this._updateMentionOverlay();
	}

	/**
	 * Dépôt d'un ou plusieurs fichiers par glisser-déposer sur la zone de saisie (#1121).
	 *
	 * ⚠️ Cette redéfinition n'est pas facultative. `BaseComponent` capture le dépôt sur
	 * **tout** composant qui en hérite et l'oriente vers `uploadFile$()`, laissée sans
	 * implémentation ici : le geste était donc avalé en silence. On le renvoie vers le
	 * chemin du trombone, qui crée le brouillon au besoin avant de joindre.
	 */
	public override onDrop(event: DragEvent): void {
		const files = event.dataTransfer ? Array.from(event.dataTransfer.files) : [];
		this.dragover = false;
		if (files.length === 0) {
			return;
		}
		event.preventDefault();
		event.stopPropagation();
		this._handleFileSelection(files);
	}

	public onFileSelected(event: any): void {
		const files: File[] = Array.from(event.target.files);
		if (files.length > 0) {
			this._handleFileSelection(files);
			// Réinitialiser l'input pour permettre de sélectionner le même fichier à nouveau
			event.target.value = '';
		}
	}

	public onRemoveAttachment(attachment: FileModel, event: Event): void {
		event.stopPropagation();
		this._handleAttachmentRemoval(attachment);
	}

	public onSelectMentionCandidate(candidate: MentionCandidate, event: Event): void {
		// mousedown + preventDefault pour ne pas faire perdre le focus au textarea avant l'insertion
		event.preventDefault();
		this._selectMention(candidate);
	}

	/*----------------------------------------------------------------------------------------
	 * Méthodes protégées
	 ----------------------------------------------------------------------------------------*/

	protected override buildForm(): void {
		this.form = this.formBuilder.group({
			content: ['']
		});
	}

	protected override save(): void {
		// Raccourci clavier compris : sans le droit, l'envoi serait refusé (#1218)
		if (!this.canSendMessage) return;
		LOGGER.debug('Début de l\'envoi du message');
		this._disableForm();
		const content = this.form.get('content')?.value || '';

		if (this._currentDraftItem && !this._creatingDraft) {
			LOGGER.debug('Envoi d\'un brouillon existant', { draftId: this._currentDraftItem.id, contentLength: content.length });
			this._sendMessage(this._currentDraftItem, content);
		} else if (this._creatingDraft) {
			LOGGER.debug('Création de brouillon en cours, attente...');
			const checkDraft = () => {
				if (this._currentDraftItem && !this._creatingDraft) {
					LOGGER.debug('Brouillon créé, envoi du message', { draftId: this._currentDraftItem.id });
					this._sendMessage(this._currentDraftItem, content);
				} else {
					LOGGER.debug('Attente de la création du brouillon...');
					setTimeout(checkDraft, 100); // Réessayer dans 100ms
				}
			};
			checkDraft();
		} else {
			LOGGER.debug('Création et envoi direct d\'un nouveau message', { contentLength: content.length });
			this._createAndSendMessage(content);
		}
	}

	/*----------------------------------------------------------------------------------------
	 * Méthodes privées
	 ----------------------------------------------------------------------------------------*/

	private _applyMentions(item: ConversationItem, content: string): void {
		// Réconciliation : on ne garde que les mentions dont le token @Label est encore présent
		// dans le texte (gère la suppression manuelle), puis on déduplique par contact.
		const internal: ClientContact[] = [];
		const external: PartnerContact[] = [];
		for (const mention of this._selectedMentions) {
			if (!content.includes(`@${mention.label}`)) {
				continue;
			}
			if (mention.isInternal) {
				if (!internal.some(contact => contact.id === mention.contact.id)) {
					internal.push(mention.contact as ClientContact);
				}
			} else {
				if (!external.some(contact => contact.id === mention.contact.id)) {
					external.push(mention.contact as PartnerContact);
				}
			}
		}
		item.mentionsInternal = internal;
		item.mentionsExternal = external;
	}

	private _autoSaveDraft(content: string): void {
		if (this._currentDraftItem) {
			LOGGER.debug('Mise à jour du brouillon existant', { draftId: this._currentDraftItem.id, contentLength: content?.length || 0 });
			this._currentDraftItem.content = content || '';
			this._applyMentions(this._currentDraftItem, content || '');
			this.conversationItemService.save$(this._currentDraftItem, {
				showMessages: false,
				emitEvent: false,
				select: false
			}).subscribe({
				next: () => {
					LOGGER.debug('Brouillon mis à jour avec succès');
					this.form.markAsPristine();
				},
				error: (error) => LOGGER.error('Erreur lors de la mise à jour du brouillon', error)
			});
		} else if (!this._creatingDraft) {
			if (!content || content.trim().length === 0) {
				LOGGER.debug('Contenu vide, pas de création de brouillon');
				return;
			}

			this._createDraft(() => {
				LOGGER.debug('Auto-sauvegarde terminée');
			});
		} else {
			LOGGER.debug('Création de brouillon déjà en cours, ignorer cette requête');
		}
	}

	private _closeMentionOverlay(): void {
		this.mentionOverlayVisible = false;
		this.filteredMentionCandidates = [];
		this.activeMentionIndex = 0;
		this._mentionTokenStart = -1;
	}

	private _createAndSendMessage(content: string): void {
		LOGGER.debug('Création puis envoi d\'un nouveau message', { contentLength: content.length });
		this._ensureConversationPersisted(
			() => {
				let item = ConversationItem.init();
				item.content = content;
				this._applyMentions(item, content);

				this.conversationService.createRelated$(item, this.conversation, 'items', {
					showMessages: false,
				}).subscribe({
					next: (item) => {
						LOGGER.debug('Message créé, procédure d\'envoi', { itemId: item.id });
						this._sendMessage(item, content);
					},
					error: (error) => {
						LOGGER.error('Erreur lors de la création du message', error);
						this._enableForm();
					}
				});
			},
			() => this._enableForm()
		);
	}

	private _createDraft(callback: () => void): void {
		LOGGER.debug('Création d\'un nouveau brouillon');
		// Flag posé de façon synchrone : bloque toute ré-entrée (auto-save, PJ) pendant la
		// persistance lazy éventuelle de la conversation puis la création du brouillon.
		this._creatingDraft = true;
		this._ensureConversationPersisted(
			() => {
				let draftItem = ConversationItem.init();
				draftItem.content = this.form.get('content')?.value || '';
				this._applyMentions(draftItem, draftItem.content || '');

				this.conversationService.createRelated$(draftItem, this.conversation, 'items', {
					showMessages: false,
					emitEvent: false,
					select: false
				}).subscribe({
					next: (item) => {
						LOGGER.debug('Brouillon créé avec succès', { draftId: item.id });
						this._currentDraftItem = item;
						this.conversation.items.push(item);
						this._creatingDraft = false;
						this.form.markAsPristine();
						callback();
					},
					error: (error) => {
						LOGGER.error('Erreur lors de la création du brouillon', error);
						this._creatingDraft = false;
						callback(); // Callback même en cas d'erreur pour ne pas bloquer
					}
				});
			},
			() => {
				this._creatingDraft = false;
				callback(); // Callback même en cas d'erreur pour ne pas bloquer
			}
		);
	}

	private _detectMentionToken(text: string, caret: number): { start: number; query: string } | null {
		// On remonte depuis le caret tant qu'on est sur des caractères non blancs et non-@.
		let i = caret - 1;
		while (i >= 0 && !/\s/.test(text[i]) && text[i] !== '@') {
			i--;
		}
		// Token valide : un @ précédé d'un début de chaîne ou d'un blanc.
		if (i >= 0 && text[i] === '@' && (i === 0 || /\s/.test(text[i - 1]))) {
			return { start: i, query: text.substring(i + 1, caret) };
		}
		return null;
	}

	private _disableForm(): void {
		this.form.disable();
	}

	private _enableForm(): void {
		this.form.enable();
	}

	private _ensureConversationPersisted(onReady: () => void, onError: () => void): void {
		// La conversation publique est créée en lazy : tant qu'elle n'est pas persistée (flag
		// méta `new`, l'id étant un id temporaire non fiable), le fil est virtuel. Au premier
		// écrit serveur (brouillon, PJ ou envoi direct), on la persiste, on stampe son id sur
		// l'instance courante, puis on enchaîne l'écrit.
		if (!this.conversation.new) {
			onReady();
			return;
		}

		// Une persistance est déjà en cours (ré-entrance) : on attend qu'elle aboutisse.
		if (this._persistingConversation) {
			const check = () => {
				if (!this.conversation.new) {
					onReady();
				} else if (!this._persistingConversation) {
					onError();
				} else {
					setTimeout(check, 100);
				}
			};
			setTimeout(check, 100);
			return;
		}

		this._persistingConversation = true;
		this.conversationService.save$(this.conversation, {
			showMessages: false,
		}).subscribe({
			next: (created: Conversation) => {
				LOGGER.debug('Conversation publique persistée en lazy', { conversationId: created.id });
				this.conversation.id = created.id;
				this.conversation.new = false;
				this._persistingConversation = false;
				this.conversationCreated.emit(this.conversation);
				onReady();
			},
			error: (error) => {
				LOGGER.error('Erreur lors de la persistance lazy de la conversation', error);
				this._persistingConversation = false;
				onError();
			}
		});
	}

	private _ensureDraftExists(): Promise<void> {
		return new Promise((resolve) => {
			if (this._creatingDraft) {
				// Attendre que la création soit terminée
				const checkDraft = () => {
					if (this._currentDraftItem && !this._creatingDraft) {
						resolve();
					} else {
						setTimeout(checkDraft, 100);
					}
				};
				checkDraft();
			} else {
				this._createDraft(resolve);
			}
		});
	}

	private _handleAttachmentClick(attachment: FileModel): void {
		if (attachment.isPdf) {
			this.fileService.downloadFile$(attachment.id, false).subscribe(blob => {
				const blobURL = URL.createObjectURL(blob);
				const newTab = window.open(blobURL, '_blank');
				if (newTab) {
					newTab.addEventListener('beforeunload', () => {
						URL.revokeObjectURL(blobURL);
					});
				}
			});
		} else {
			this.fileService.downloadFile$(attachment.id, false).subscribe(blob => {
				const link = document.createElement('a');
				link.href = window.URL.createObjectURL(blob);
				link.download = attachment.fileName;
				link.click();
				window.URL.revokeObjectURL(link.href);
			});
		}
	}

	/**
	 * Retire une pièce du brouillon de message.
	 *
	 * Le geste passe par le message et non par le fichier : `/api/files/` est en
	 * lecture seule depuis `#1052`, et un `PATCH` avec `deleted: true` y répond 405.
	 */
	private _handleAttachmentRemoval(attachment: FileModel): void {
		if (!this._currentDraftItem) return;

		this.conversationItemService.detachAttachment$(this._currentDraftItem, attachment.id).subscribe(() => {
			this._loadAttachments();
		});
	}

	private _handleFileSelection(files: File[]): void {
		if (!this._currentDraftItem) {
			this._ensureDraftExists().then(() => {
				this._uploadFiles(files);
			});
		} else {
			this._uploadFiles(files);
		}
	}

	private _handleMentionKeydown(event: KeyboardEvent): void {
		if (!this.mentionOverlayVisible || this.filteredMentionCandidates.length === 0) {
			return;
		}
		switch (event.key) {
			case 'ArrowDown':
				event.preventDefault();
				this.activeMentionIndex = (this.activeMentionIndex + 1) % this.filteredMentionCandidates.length;
				break;
			case 'ArrowUp':
				event.preventDefault();
				this.activeMentionIndex = (this.activeMentionIndex - 1 + this.filteredMentionCandidates.length) % this.filteredMentionCandidates.length;
				break;
			case 'Enter':
				event.preventDefault();
				this._selectMention(this.filteredMentionCandidates[this.activeMentionIndex]);
				break;
			case 'Escape':
				event.preventDefault();
				this._closeMentionOverlay();
				break;
		}
	}

	private _initializeForm(): void {
		this._selectedMentions = [];
		this._closeMentionOverlay();
		this._loadMentionCandidates();
		this._prefillForm();
		this._setupAutoSave();
	}

	private _loadAttachments(): void {
		if (this._currentDraftItem) {
			this.conversationItemService.getAttachments$(this._currentDraftItem).subscribe(
				attachments => {
					this.attachments = attachments;
					LOGGER.debug('Pièces jointes chargées', { count: attachments.length });
				}
			);
		} else {
			this.attachments = [];
		}
	}

	private _loadMentionCandidates(): void {
		this._mentionCandidates = [];
		if (!this._conversation) {
			return;
		}
		if (this._conversation.private) {
			// Conv privée : seuls les participants (internes + externes) sont mentionnables,
			// en excluant l'utilisateur courant (on ne se mentionne pas soi-même)
			const internal = (this._conversation.internalParticipants || [])
				.filter(contact => contact.user?.id !== this.appService.user!.id)
				.map(contact => this._toMentionCandidate(contact, true));
			const external = (this._conversation.externalParticipants || []).map(contact => this._toMentionCandidate(contact, false));
			this._mentionCandidates = [...internal, ...external];
		} else {
			// Conv publique : tout ClientContact du client (externes interdits côté backend)
			const filter: Filter = new Filter({
				property: 'user.id',
				comparator: Filter.COMPARATOR.NE,
				value: this.appService.user!.id
			});
			this.clientContactService.getList$({ filters: [filter] }).subscribe((contacts: ClientContact[]) => {
				this._mentionCandidates = contacts.map(contact => this._toMentionCandidate(contact, true));
				// Si un @ est déjà saisi au moment où les candidats arrivent, on ouvre l'overlay
				this._updateMentionOverlay();
			});
		}
	}

	private _prefillForm(): void {
		if (this.conversation && this.conversation.items.length > 0) {
			// Si un item de la conversation est au statut "brouillon", on le met dans le formulaire
			let draftItem: ConversationItem | undefined = this._conversation.items.find(item => item.hasStatus(this.Status.Draft));
			if (draftItem) {
				LOGGER.debug('Brouillon trouvé, pré-remplissage du formulaire', { draftId: draftItem.id, contentLength: draftItem.content?.length || 0 });
				this._currentDraftItem = draftItem;
				this.form.get('content')?.setValue(draftItem.content);
				this._selectedMentions = [
					...(draftItem.mentionsInternal || []).map(contact => this._toMentionCandidate(contact, true)),
					...(draftItem.mentionsExternal || []).map(contact => this._toMentionCandidate(contact, false)),
				];
				this._loadAttachments();
			} else {
				LOGGER.debug('Aucun brouillon trouvé pour cette conversation');
				this.attachments = [];
			}
		} else {
			LOGGER.debug('Aucun élément dans la conversation ou conversation non définie');
		}
	}

	private _selectMention(candidate: MentionCandidate): void {
		const textarea = this._contentTextarea?.nativeElement;
		if (!textarea || this._mentionTokenStart < 0) {
			this._closeMentionOverlay();
			return;
		}
		const text = textarea.value || '';
		const caret = textarea.selectionStart ?? text.length;
		const before = text.substring(0, this._mentionTokenStart);
		const after = text.substring(caret);
		const insertion = `@${candidate.label} `;
		const newValue = before + insertion + after;

		this.form.get('content')!.setValue(newValue);
		this.form.get('content')!.markAsDirty();

		// L'id est capturé ici (à la sélection), pas deviné depuis le texte
		if (!this._selectedMentions.some(mention => mention.id === candidate.id && mention.isInternal === candidate.isInternal)) {
			this._selectedMentions.push(candidate);
		}

		this._closeMentionOverlay();

		// Repositionne le caret juste après le token inséré
		const newCaret = before.length + insertion.length;
		setTimeout(() => {
			textarea.focus();
			textarea.setSelectionRange(newCaret, newCaret);
		});
	}

	private _sendMessage(draftItem: ConversationItem, content: string): void {
		LOGGER.debug('Envoi du message', { itemId: draftItem.id, contentLength: content.length });
		// Mettre à jour le contenu du brouillon
		draftItem.content = content;

		// Envoyer le message via l'action /send
		this.conversationItemService.send$(draftItem)
			.pipe(
				finalize(() => {
					LOGGER.debug('Fin de l\'envoi du message, réactivation du formulaire');
					this._enableForm();
				})
			)
			.subscribe({
				next: (item) => {
					LOGGER.debug('Message envoyé avec succès', { itemId: item.id });
					// Remplacer le brouillon par le message envoyé
					const index = this.conversation.items.findIndex(i => i.id === draftItem.id);
					if (index > -1) {
						this.conversation.items[index] = item;
						LOGGER.debug('Brouillon remplacé par le message envoyé dans la liste');
					} else {
						LOGGER.warn('Brouillon non trouvé dans la liste des items de la conversation, ajout du message envoyé');
						this.conversation.items.push(item);
					}
					this.form.reset({ emitEvent: false });
					this._currentDraftItem = undefined;
					this.attachments = [];
					this._selectedMentions = [];
					this._closeMentionOverlay();
					this.itemSaved.emit(item);
				},
				error: (error) => {
					LOGGER.error('Erreur lors de l\'envoi du message', error);
				}
			});
	}

	private _setupAutoSave(): void {
		LOGGER.debug('Configuration de la sauvegarde automatique');

		if (this.form && this.form.get('content')) {
			this.subscriptions.add(
				this.form.get('content')!.valueChanges
					.pipe(
						debounceTime(1000), // Attendre 1 seconde après la dernière saisie
						distinctUntilChanged() // Ne déclencher que si la valeur a changé
					)
					.subscribe(content => {
						LOGGER.debug('Changement détecté dans le formulaire, déclenchement de la sauvegarde automatique', { contentLength: content?.length || 0 });
						this._autoSaveDraft(content);
					})
			);
			LOGGER.debug('Sauvegarde automatique configurée avec succès');
		} else {
			LOGGER.warn('Impossible de configurer la sauvegarde automatique - formulaire ou champ content non disponible');
		}
	}

	private _toMentionCandidate(contact: ClientContact | PartnerContact, isInternal: boolean): MentionCandidate {
		return {
			id: contact.id,
			label: contact.fullName,
			isInternal: isInternal,
			contact: contact,
		};
	}

	private _updateMentionOverlay(): void {
		const textarea = this._contentTextarea?.nativeElement;
		if (!textarea) {
			this._closeMentionOverlay();
			return;
		}
		const text = textarea.value || '';
		const caret = textarea.selectionStart ?? text.length;
		const token = this._detectMentionToken(text, caret);
		if (!token) {
			this._closeMentionOverlay();
			return;
		}
		const query = token.query.toLowerCase();
		this.filteredMentionCandidates = this._mentionCandidates
			.filter(candidate => candidate.label.toLowerCase().includes(query))
			.slice(0, 8);
		if (this.filteredMentionCandidates.length === 0) {
			this._closeMentionOverlay();
			return;
		}
		this._mentionTokenStart = token.start;
		this.activeMentionIndex = 0;
		this.mentionOverlayVisible = true;
	}

	private _uploadFiles(files: File[]): void {
		if (!this._currentDraftItem) {
			LOGGER.error('Impossible d\'uploader des fichiers sans brouillon');
			return;
		}

		LOGGER.debug('Upload de fichiers', { count: files.length, draftId: this._currentDraftItem.id });
		this.appService.uiBlocked = true;

		let requests$ = [];
		for (let i = 0; i < files.length; i++) {
			requests$.push(this.conversationItemService.uploadAttachment$(this._currentDraftItem, files[i]));
		}

		forkJoin(requests$).subscribe({
			next: () => {
				LOGGER.debug('Tous les fichiers ont été uploadés avec succès');
				this.appService.uiBlocked = false;
				this._loadAttachments();
			},
			error: (error) => {
				LOGGER.error('Erreur lors de l\'upload des fichiers', error);
				this.appService.uiBlocked = false;
			}
		});
	}

}
